// LLM generated - Model Claude 4 Sonnet
import React, { useMemo } from 'react';
import { Stack, Box } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import useStyles from './ServiceFeeModal.styles';

import { IconCircleMinus } from '@tabler/icons-react';

import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import { Title } from '~/domains/design-system/Title';
import { Text } from '~/domains/design-system/Text';
import { Button } from '~/domains/design-system/Button';
import { IconAlertTriangle } from '~/domains/design-system/icons/IconAlertTriangle';
import IconLock from '~/domains/design-system/icons/IconLock';
import { SERVICE_FEE_ALERT_TYPES } from '~/domains/contest/domains/common/constants';
import type { ServiceFeeAlertType } from '~/domains/contest/domains/common/constants';

type ServiceFeeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  alertType: ServiceFeeAlertType;
  commissionerName?: string;
  commissionerUsername?: string;
};

function ServiceFeeModal({
  isOpen,
  onClose,
  alertType,
  commissionerName,
  commissionerUsername,
}: ServiceFeeModalProps) {
  const { t } = useTranslation('manage-contest');
  const { classes, theme } = useStyles();

  const { icon, title, description } = useMemo(() => {
    switch (alertType) {
      case SERVICE_FEE_ALERT_TYPES.SERVICE_FEE_CANCELLED:
        return {
          icon: <IconCircleMinus size={40} color={theme.colors.red[3]} />,
          title: t('serviceFeeModal.serviceFeeCancelled.heading'),
          description: t('serviceFeeModal.serviceFeeCancelled.description'),
        };
      case SERVICE_FEE_ALERT_TYPES.SERVICE_FEE_OVERDUE:
        return {
          icon: <IconLock size={40} color={theme.colors.gray2[6]} />,
          title: t('serviceFeeModal.serviceFeeOverdue.heading'),
          description: t('serviceFeeModal.serviceFeeOverdue.description'),
        };
      default:
        return {
          icon: <IconAlertTriangle size={40} color={theme.colors.gray2[6]} />,
          title: t('serviceFeeModal.serviceFeedue.heading'),
          description: t('serviceFeeModal.serviceFeedue.description'),
        };
    }
  }, [alertType, t]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      type={{
        desktop: 'modal',
        mobile: 'drawer',
      }}
    >
      <DialogBody>
        <Stack spacing={theme.other.spacing._32} align="center" className={classes.contentStack}>
          {/* Icon and Content Section */}
          <Stack spacing={theme.other.spacing._32} align="center" w="100%">
            <Stack spacing={theme.other.spacing._8} align="center">
              {/* Icon */}
              <Box className={classes.iconContainer}>{icon}</Box>

              {/* Title */}
              <Title
                variant="subhead-x-large"
                align="center"
                color="gray2.6"
                className={classes.title}
              >
                {title}
              </Title>

              {/* Description */}
              <Text
                variant="body-large"
                align="center"
                color="gray2.4"
                className={classes.description}
              >
                {description}
              </Text>
            </Stack>

            {/* Commissioner Card */}
            {commissionerName && (
              <Box className={classes.commissionerCard}>
                {/* Commissioner Label */}
                <Box className={classes.commissionerLabel}>
                  <Text variant="title-small" color="gray2.3">
                    {t('serviceFeeModal.commissioner')}
                  </Text>
                </Box>

                {/* Commissioner Info */}
                <Stack spacing={0} align="center">
                  <Text variant="subhead-large" className={classes.commissionerName}>
                    {commissionerName}
                  </Text>
                  {commissionerUsername && (
                    <Text variant="title-small" color="gray2.4">
                      @{commissionerUsername}
                    </Text>
                  )}
                </Stack>
              </Box>
            )}
          </Stack>

          {/* Dismiss Button */}
          <Box className={classes.buttonContainer}>
            <Button variant="tertiary-fill" size="large" onClick={onClose} fullWidth>
              {t('serviceFeeModal.dismiss')}
            </Button>
          </Box>
        </Stack>
      </DialogBody>
    </Dialog>
  );
}

export default ServiceFeeModal;
